import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Conflict {
  id: string;
  row_number: number;
  conflict_type: string;
  existing_data: {
    name: string;
    email?: string;
    document?: string;
  };
  new_data: {
    name: string;
    email?: string;
    document?: string;
  };
}

interface ConflictResolutionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importId: string;
  conflicts: Conflict[];
  onResolved: () => void;
}

export function ConflictResolutionModal({
  open,
  onOpenChange,
  importId,
  conflicts,
  onResolved,
}: ConflictResolutionModalProps) {
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleResolutionChange = (conflictId: string, resolution: string) => {
    setResolutions((prev) => ({ ...prev, [conflictId]: resolution }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('resolve-conflicts', {
        body: {
          import_id: importId,
          resolutions: conflicts.map((conflict) => ({
            conflict_id: conflict.id,
            resolution: resolutions[conflict.id] || 'keep_existing',
          })),
        },
      });

      if (error) throw error;

      toast.success('Conflitos resolvidos com sucesso!');
      onResolved();
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Erro ao resolver conflitos: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Conflitos Encontrados na Importação
          </DialogTitle>
          <DialogDescription>
            Foram encontrados {conflicts.length} conflito(s) que precisam ser resolvidos antes de continuar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {conflicts.map((conflict) => (
            <div key={conflict.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Linha {conflict.row_number}</h4>
                <span className="text-sm text-muted-foreground">
                  {conflict.existing_data.email || conflict.existing_data.document}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Nome atual no sistema:</p>
                  <p className="font-medium">{conflict.existing_data.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Nome na planilha:</p>
                  <p className="font-medium">{conflict.new_data.name}</p>
                </div>
              </div>

              <RadioGroup
                value={resolutions[conflict.id] || 'keep_existing'}
                onValueChange={(value) => handleResolutionChange(conflict.id, value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="keep_existing" id={`keep-${conflict.id}`} />
                  <Label htmlFor={`keep-${conflict.id}`} className="cursor-pointer">
                    Manter nome atual ({conflict.existing_data.name})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="update_name" id={`update-${conflict.id}`} />
                  <Label htmlFor={`update-${conflict.id}`} className="cursor-pointer">
                    Atualizar para novo nome ({conflict.new_data.name})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="create_new" id={`create-${conflict.id}`} />
                  <Label htmlFor={`create-${conflict.id}`} className="cursor-pointer">
                    Criar novo cliente separado
                  </Label>
                </div>
              </RadioGroup>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Aplicando...' : 'Aplicar Resoluções'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
